import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Container,
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  LinearProgress,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { register as registerAction } from '../../store/authSlice';
import { AppDispatch, RootState } from '../../store';
import { RegisterRequest } from '../../types';

const schema = yup.object({
  name: yup.string().required('Name is required'),
  email: yup.string().email('Invalid email').required('Email is required'),
  // PRE_LAUNCH L2 / H6 — register-side policy mirrors the backend
  // validatePasswordPolicy (min 12, 3 character classes).
  password: yup.string()
    .min(12, 'Password must be at least 12 characters')
    .test('classes', 'Password needs at least 3 of: lowercase, UPPERCASE, digits, symbols',
      v => !!v && [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].filter(re => re.test(v)).length >= 3)
    .required('Password is required'),
});

// PRE_LAUNCH L2 — live strength scoring. Score 0-4 across length + class
// diversity; common-password short-list always returns 0.
const COMMON_PWS = new Set(['password', 'password123', '12345678', 'qwerty', 'letmein', 'welcome', 'admin']);
function passwordScore(pw: string): { score: 0 | 1 | 2 | 3 | 4; label: string; color: 'error' | 'warning' | 'info' | 'success' } {
  if (!pw) return { score: 0, label: '—', color: 'error' };
  if (COMMON_PWS.has(pw.toLowerCase())) return { score: 0, label: 'Too common', color: 'error' };
  let s = 0;
  if (pw.length >= 12) s++;
  if (pw.length >= 16) s++;
  const classes = [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].filter(re => re.test(pw)).length;
  if (classes >= 3) s++;
  if (classes === 4) s++;
  const score = Math.min(4, s) as 0 | 1 | 2 | 3 | 4;
  const labels = ['Very weak', 'Weak', 'Fair', 'Strong', 'Very strong'];
  const colors: Array<'error' | 'warning' | 'info' | 'success'> = ['error', 'error', 'warning', 'info', 'success'];
  return { score, label: labels[score], color: colors[score] };
}

const RegisterForm: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { loading, error } = useSelector((state: RootState) => state.auth);
  const [registered, setRegistered] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');

  const { register, handleSubmit, formState: { errors }, getValues, watch } = useForm<RegisterRequest>({
    resolver: yupResolver(schema)
  });
  const pwValue = watch('password') || '';
  const pwScore = passwordScore(pwValue);

  const onSubmit = async (data: RegisterRequest) => {
    const result = await dispatch(registerAction(data));
    if (registerAction.fulfilled.match(result)) {
      // Show pending approval message — do NOT redirect to dashboard
      setRegisteredEmail(data.email);
      setRegistered(true);
    }
  };

  if (registered) {
    return (
      <Container component="main" maxWidth="xs">
        <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Paper elevation={3} sx={{ p: 4, width: '100%', textAlign: 'center' }}>
            <HourglassTopIcon sx={{ fontSize: 56, color: '#f59e0b', mb: 2 }} />
            <Typography component="h1" variant="h5" gutterBottom fontWeight={700}>
              Registration Received
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              Your account (<strong>{registeredEmail}</strong>) has been created and is pending approval by the administrator.
            </Typography>
            <Alert severity="info" sx={{ textAlign: 'left', mb: 2 }}>
              You will be able to log in once the administrator activates your account. Please contact the admin directly if you need urgent access.
            </Alert>
            <Button component={Link} to="/login" variant="outlined" fullWidth>
              Back to Login
            </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Typography component="h1" variant="h5" align="center" gutterBottom>
            Create Account
          </Typography>
          <Typography variant="subtitle1" align="center" color="text.secondary" gutterBottom>
            Join Trading Dashboard
          </Typography>

          <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 3 }}>
            <TextField
              margin="normal"
              fullWidth
              label="Full Name"
              autoFocus
              {...register('name')}
              error={!!errors.name}
              helperText={errors.name?.message}
            />
            <TextField
              margin="normal"
              fullWidth
              label="Email Address"
              autoComplete="email"
              {...register('email')}
              error={!!errors.email}
              helperText={errors.email?.message}
            />
            <TextField
              margin="normal"
              fullWidth
              label="Password"
              type="password"
              autoComplete="new-password"
              {...register('password')}
              error={!!errors.password}
              helperText={errors.password?.message || 'Min 12 chars, 3 of: lowercase / UPPERCASE / digit / symbol'}
            />
            {/* PRE_LAUNCH L2 — live password-strength meter. */}
            {pwValue && (
              <Box sx={{ mt: 0.5, mb: 0.5 }}>
                <LinearProgress
                  variant="determinate"
                  value={(pwScore.score / 4) * 100}
                  color={pwScore.color}
                  sx={{ height: 6, borderRadius: 3 }}
                />
                <Typography variant="caption" color={`${pwScore.color}.main`} sx={{ fontWeight: 600 }}>
                  Strength: {pwScore.label}
                </Typography>
              </Box>
            )}

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Request Access'}
            </Button>

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2">
                Already have an account?{' '}
                <Link to="/login" style={{ textDecoration: 'none' }}>
                  Sign In
                </Link>
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default RegisterForm;
